import { useState, useEffect, useRef, useCallback } from 'react'
import cutileiApi from 'services/cutileiApi'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import { CHAT_MODES, formatPhone, formatMessageTime, chatPath } from 'services/Whatsapp'
import MainButton from 'components/Buttons/MainButton'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import useSyncing from './useSyncing'

import {
  ChatLayout,
  ChatList,
  ChatItem,
  ChatName,
  ChatPreview,
  ModeBadge,
  ChatPanel,
  ChatHeader,
  ModeButton,
  Messages,
  Bubble,
  Composer,
  TextArea,
  EmptyPanel,
  Row,
  Caption,
  ErrorText,
  SyncBanner,
  ModeLabel
} from './styles'

/*
  As respostas do bot não chegam por evento (o bot manda direto pelo WhatsApp): a conversa
  aberta é relida a cada 10 segundos e a lista a cada 30.
*/
export const MESSAGES_REFRESH_MS = 10000
export const LIST_REFRESH_MS = 30000
/* Enquanto o histórico do celular chega, a lista é relida mais vezes. */
export const SYNC_REFRESH_MS = 5000

/* Perto do fim (até 80px): só aí a conversa desce sozinha quando chega mensagem. */
const NEAR_BOTTOM_PX = 80

const MAX_TEXT = 4000

const chatTitle = chat => chat.name || formatPhone(chat.phone) || 'Contato'

// Bot geral desligado e ninguém escolheu um modo para esta conversa: o bot não responde nela
const isBotOff = chat => chat.automatic && chat.mode === 'human'
const BOT_OFF = { label: 'Sem bot', color: '#B0B0AC' }
const badgeOf = chat => (isBotOff(chat) ? BOT_OFF : CHAT_MODES[chat.mode])

const untilText = chat => {
  if (isBotOff(chat)) return 'O atendimento automático do salão está desligado. Clique em Bot para ele responder só esta conversa.'
  if (chat.mode !== 'human') return CHAT_MODES[chat.mode]?.description
  if (!chat.paused_until) return 'A equipe está com a conversa até alguém devolver para o bot.'
  const time = new Date(chat.paused_until).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })
  return `A equipe está com a conversa; o bot volta às ${time}.`
}

/*
  As conversas do WhatsApp do salão: a lista, a conversa aberta com rolagem própria, responder
  como equipe e escolher quem atende (bot, equipe ou ninguém). As mensagens são lidas do
  WhatsApp na hora; nada fica guardado no banco da Cutilei.
*/
export default function Chats({ businessId, socket, session }) {
  const [chats, setChats] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [messages, setMessages] = useState([])
  const [loadingMessages, setLoadingMessages] = useState(false)
  const [text, setText] = useState('')
  const [sending, setSending] = useState(false)
  const [savingMode, setSavingMode] = useState(false)
  const [error, setError] = useState(null)
  const scroller = useRef(null)
  const stickToBottom = useRef(true)
  const selectedRef = useRef(null)
  selectedRef.current = selectedId
  const syncing = useSyncing(session)

  const loadChats = useCallback(async () => {
    try {
      const { data } = await cutileiApi.get(`/businesses/${businessId}/whatsapp/chats`)
      setChats(data.chats)
      setError(null)
    } catch (err) {
      logApiError(err, 'whatsapp.chats')
      setChats(current => current ?? [])
      setError(getApiErrorMessage(err, 'Não foi possível carregar as conversas.'))
    }
  }, [businessId])

  const loadMessages = useCallback(async chatId => {
    try {
      const { data } = await cutileiApi.get(`${chatPath(businessId, chatId)}/messages`)
      if (selectedRef.current === chatId) setMessages(data.messages)
    } catch (err) {
      logApiError(err, 'whatsapp.messages')
    }
  }, [businessId])

  useEffect(() => {
    loadChats()
    const timer = setInterval(loadChats, syncing ? SYNC_REFRESH_MS : LIST_REFRESH_MS)
    return () => clearInterval(timer)
  }, [loadChats, syncing])

  useEffect(() => {
    if (!selectedId) return undefined
    setLoadingMessages(true)
    setMessages([])
    stickToBottom.current = true
    loadMessages(selectedId).finally(() => setLoadingMessages(false))
    const timer = setInterval(() => loadMessages(selectedId), MESSAGES_REFRESH_MS)
    return () => clearInterval(timer)
  }, [selectedId, loadMessages])

  // Tempo real: mensagem nova da cliente e mudança de modo (feita aqui ou em outra tela)
  useEffect(() => {
    if (!socket) return undefined
    const onMessage = ({ chat_id: chatId, message }) => {
      if (chatId === selectedRef.current) {
        setMessages(current => (current.some(m => m.id === message.id) ? current : [...current, message]))
      }
      setChats(current => {
        if (!current) return current
        const found = current.find(c => c.chat_id === chatId)
        if (!found) {
          loadChats()
          return current
        }
        return [{ ...found, last_message: message }, ...current.filter(c => c.chat_id !== chatId)]
      })
    }
    const onMode = update => {
      setChats(current => current?.map(c => (c.chat_id === update.chat_id ? { ...c, ...update } : c)) ?? current)
    }
    socket.on(`${businessId}_whatsapp_message`, onMessage)
    socket.on(`${businessId}_whatsapp_mode`, onMode)
    return () => {
      socket.off(`${businessId}_whatsapp_message`, onMessage)
      socket.off(`${businessId}_whatsapp_mode`, onMode)
    }
  }, [socket, businessId, loadChats])

  // Desce até a última mensagem ao abrir a conversa e quando chega mensagem, mas não puxa para
  // baixo quem subiu para ler o histórico
  useEffect(() => {
    const el = scroller.current
    if (el && stickToBottom.current) el.scrollTop = el.scrollHeight
  }, [messages])

  const handleScroll = () => {
    const el = scroller.current
    if (el) stickToBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < NEAR_BOTTOM_PX
  }

  const selected = chats?.find(c => c.chat_id === selectedId) ?? null

  const updateChat = update => setChats(current => current.map(c => (c.chat_id === update.chat_id ? { ...c, ...update } : c)))

  const handleMode = async mode => {
    setSavingMode(true)
    setError(null)
    try {
      const { data } = await cutileiApi.put(`${chatPath(businessId, selectedId)}/mode`, { mode })
      updateChat(data)
    } catch (err) {
      logApiError(err, 'whatsapp.mode')
      setError(getApiErrorMessage(err, 'Não foi possível mudar quem atende esta conversa.'))
    } finally {
      setSavingMode(false)
    }
  }

  const handleSend = async () => {
    const body = text.trim()
    if (!body || sending) return
    setSending(true)
    setError(null)
    try {
      const { data } = await cutileiApi.post(`${chatPath(businessId, selectedId)}/messages`, { text: body })
      const message = { id: `local-${Date.now()}`, from_me: true, body, has_media: false, timestamp: Math.floor(Date.now() / 1000) }
      stickToBottom.current = true
      setMessages(current => [...current, message])
      updateChat({ ...data, last_message: message })
      setText('')
    } catch (err) {
      logApiError(err, 'whatsapp.reply')
      setError(getApiErrorMessage(err, 'A mensagem não foi enviada. Tente de novo.'))
    } finally {
      setSending(false)
    }
  }

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  if (chats === null) return <Caption>Carregando as conversas...</Caption>

  return (
    <>
      {error && <ErrorText>{error}</ErrorText>}
      {syncing && (
        <SyncBanner role='status'>
          <Loading/>
          <span>Sincronizando as conversas do celular. Elas vão aparecendo aqui nos próximos minutos.</span>
        </SyncBanner>
      )}
      <ChatLayout $offset={syncing ? 280 : 220}>
        <ChatList aria-label='Conversas'>
          {chats.length === 0 && <EmptyPanel>{syncing ? 'Sincronizando...' : 'Nenhuma conversa ainda.'}</EmptyPanel>}
          {chats.map(chat => (
            <ChatItem key={chat.chat_id} type='button' $selected={chat.chat_id === selectedId} onClick={() => setSelectedId(chat.chat_id)}>
              <Row $justify='space-between' $gap={6}>
                <ChatName>{chatTitle(chat)}</ChatName>
                <ModeBadge $color={badgeOf(chat)?.color}>{badgeOf(chat)?.label}</ModeBadge>
              </Row>
              <Row $justify='space-between' $gap={6} style={{ flexWrap: 'nowrap' }}>
                <ChatPreview $grow>
                  {chat.last_message ? `${chat.last_message.from_me ? 'Você: ' : ''}${chat.last_message.body || (chat.last_message.has_media ? 'Mídia' : '')}` : ''}
                </ChatPreview>
                <ChatPreview>{formatMessageTime(chat.last_message?.timestamp)}</ChatPreview>
              </Row>
            </ChatItem>
          ))}
        </ChatList>

        <ChatPanel>
          {!selected ? (
            <EmptyPanel>Escolha uma conversa para ver as mensagens.</EmptyPanel>
          ) : (
            <>
              <ChatHeader>
                <div>
                  <ChatName>{chatTitle(selected)}</ChatName>
                  <Caption>{untilText(selected)}</Caption>
                </div>
                <Row $gap={6} role='group' aria-label='Quem atende esta conversa'>
                  <ModeLabel>Quem responde:</ModeLabel>
                  {Object.entries(CHAT_MODES).map(([mode, info]) => (
                    <ModeButton
                      key={mode}
                      type='button'
                      $color={info.color}
                      $selected={selected.mode === mode && !isBotOff(selected)}
                      aria-pressed={selected.mode === mode && !isBotOff(selected)}
                      disabled={savingMode || (!selected.phone && !selected.chat_id.endsWith('@lid'))}
                      onClick={() => handleMode(mode)}
                    >
                      {info.label}
                    </ModeButton>
                  ))}
                </Row>
              </ChatHeader>

              <Messages ref={scroller} onScroll={handleScroll} aria-label='Mensagens'>
                {loadingMessages && messages.length === 0 && <Caption>Carregando...</Caption>}
                {messages.map(m => (
                  <Bubble key={m.id} $mine={m.from_me}>
                    {m.body || (m.has_media ? <i>Mídia (abra no celular para ver)</i> : '')}
                    <small>{formatMessageTime(m.timestamp)}</small>
                  </Bubble>
                ))}
              </Messages>

              <Composer>
                <TextArea
                  aria-label='Mensagem'
                  placeholder='Responder como equipe (Enter envia, Shift+Enter quebra a linha)'
                  rows={1}
                  value={text}
                  maxLength={MAX_TEXT}
                  onChange={e => setText(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
                <MainButton label='Enviar' width={100} loading={sending} disabled={!text.trim()} onClick={handleSend}/>
              </Composer>
            </>
          )}
        </ChatPanel>
      </ChatLayout>
      <Caption>Responder por aqui passa a conversa para a equipe por 4 horas; o bot não responde enquanto isso.</Caption>
    </>
  )
}
